import { QRCodeSVG } from "qrcode.react";
import { Sparkles, Star, Heart, Phone, Printer, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const SITE_URL = "https://thebabysittingclub.net";

export default function Poster() {
  return (
    <div className="min-h-screen bg-[#FFFAEB] font-body">
      {/* Toolbar (hidden when printing) */}
      <div className="print:hidden sticky top-0 z-50 bg-[#FFFAEB]/95 backdrop-blur border-b-4 border-gray-900">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <Link
            to="/"
            data-testid="poster-back"
            className="inline-flex items-center gap-2 bg-white border-2 border-gray-900 rounded-full px-4 py-2 font-bold text-sm shadow-pop-sm btn-pop"
          >
            <ArrowLeft size={16} /> Back to site
          </Link>
          <p className="text-sm font-bold text-gray-700">
            🖨 Click <span className="text-[#FF6B9E]">Print Poster</span> to print or save as PDF.
          </p>
          <button
            data-testid="poster-print-btn"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 bg-[#FF6B9E] text-white border-2 border-gray-900 rounded-full px-5 py-2 font-display text-base shadow-pop-sm btn-pop"
          >
            <Printer size={18} /> Print Poster
          </button>
        </div>
      </div>

      {/* Poster Page (A4 portrait friendly) */}
      <div className="px-4 py-8 print:p-0 print:py-0 flex justify-center">
        <div
          data-testid="poster-sheet"
          className="poster-sheet relative w-full max-w-[800px] aspect-[210/297] bg-[#FFFAEB] border-4 border-gray-900 rounded-3xl print:rounded-none print:border-0 overflow-hidden shadow-pop-lg print:shadow-none"
        >
          {/* Decorative corners */}
          <Star
            size={70}
            className="absolute top-6 left-6 text-[#FFD166] fill-[#FFD166] -rotate-12"
          />
          <Heart
            size={56}
            className="absolute top-8 right-8 text-[#FF6B9E] fill-[#FF6B9E] rotate-12"
          />
          <Sparkles
            size={60}
            className="absolute bottom-32 left-10 text-[#4DB8FF]"
          />
          <Sparkles
            size={50}
            className="absolute bottom-20 right-10 text-[#38E5B6]"
          />

          {/* Polka dots */}
          <div className="absolute inset-0 pointer-events-none">
            {[
              { t: "8%", l: "30%", c: "#FF8E72", s: 12 },
              { t: "12%", l: "70%", c: "#38E5B6", s: 16 },
              { t: "85%", l: "20%", c: "#FFD166", s: 14 },
              { t: "78%", l: "85%", c: "#FF6B9E", s: 18 },
              { t: "45%", l: "5%", c: "#4DB8FF", s: 10 },
              { t: "55%", l: "92%", c: "#FF8E72", s: 14 },
            ].map((d, i) => (
              <span
                key={i}
                className="absolute rounded-full border-2 border-gray-900"
                style={{
                  top: d.t,
                  left: d.l,
                  width: d.s,
                  height: d.s,
                  background: d.c,
                }}
              />
            ))}
          </div>

          <div className="relative h-full flex flex-col items-center px-8 sm:px-12 py-12 text-center">
            {/* Top eyebrow */}
            <div className="bg-white border-2 border-gray-900 rounded-full px-5 py-1.5 shadow-pop-sm">
              <span className="font-bold text-xs sm:text-sm text-gray-800 uppercase tracking-widest">
                Hey Neighbors!
              </span>
            </div>

            {/* Title */}
            <h1 className="mt-4 font-display text-5xl sm:text-7xl leading-[0.9] text-gray-900">
              The{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-[#FF6B9E]">Baby</span>
                <span className="absolute inset-x-0 bottom-1 h-3 bg-[#FFD166] -z-0 -rotate-1"></span>
              </span>
              sitting
            </h1>
            <div className="mt-2 inline-block -rotate-2 bg-[#4DB8FF] text-white px-6 py-1 border-4 border-gray-900 rounded-2xl shadow-pop font-display text-4xl sm:text-6xl">
              Club!
            </div>

            {/* Subline */}
            <p className="mt-5 font-hand text-2xl sm:text-3xl text-gray-800">
              by Natania, Grace, Alena, Neytra, Sadie &amp; Elena
            </p>

            {/* Highlights */}
            <div className="mt-6 grid grid-cols-3 gap-2 sm:gap-3 w-full max-w-md">
              <div className="bg-[#FF6B9E] text-white border-4 border-gray-900 rounded-2xl py-2 -rotate-2 shadow-pop-sm">
                <p className="font-display text-lg sm:text-xl leading-none">$18</p>
                <p className="font-bold text-[10px] sm:text-xs uppercase">/ hour AUD</p>
              </div>
              <div className="bg-[#FFD166] text-gray-900 border-4 border-gray-900 rounded-2xl py-2 rotate-1 shadow-pop-sm">
                <p className="font-display text-lg sm:text-xl leading-none">2</p>
                <p className="font-bold text-[10px] sm:text-xs uppercase">Sitters / Job</p>
              </div>
              <div className="bg-[#38E5B6] text-gray-900 border-4 border-gray-900 rounded-2xl py-2 -rotate-1 shadow-pop-sm">
                <p className="font-display text-lg sm:text-xl leading-none">Ages</p>
                <p className="font-bold text-[10px] sm:text-xs uppercase">7 &amp; Under</p>
              </div>
            </div>

            {/* QR Code Block */}
            <div className="mt-7 bg-white border-4 border-gray-900 rounded-3xl p-5 sm:p-6 shadow-pop rotate-1">
              <p className="font-display text-xl sm:text-2xl text-gray-900 mb-3">
                📱 Scan to see more!
              </p>
              <div
                data-testid="poster-qr"
                className="bg-white p-3 border-2 border-gray-900 rounded-2xl inline-block"
              >
                <QRCodeSVG
                  value={SITE_URL}
                  size={180}
                  level="H"
                  bgColor="#FFFFFF"
                  fgColor="#1F2937"
                  includeMargin={false}
                />
              </div>
              <p className="mt-3 font-bold text-xs sm:text-sm text-gray-700 break-all">
                thebabysittingclub.net
              </p>
            </div>

            {/* Call us strip */}
            <div className="mt-auto pt-5 w-full">
              <div className="bg-gray-900 text-[#FFFAEB] border-4 border-gray-900 rounded-full py-3 px-5 flex items-center justify-center gap-3">
                <span className="w-9 h-9 bg-[#FF6B9E] rounded-full flex items-center justify-center border-2 border-[#FFFAEB]">
                  <Phone size={16} />
                </span>
                <p className="font-display text-xl sm:text-2xl text-[#FFD166]">
                  Call us: <span className="text-white">9482 5353</span>
                </p>
              </div>
              <p className="mt-3 font-hand text-xl text-gray-700">
                ~ fun, kind, safe babysitting based in Hornsby ~
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 0; }
          html, body { background: #FFFAEB !important; }
          .poster-sheet {
            width: 210mm !important;
            height: 297mm !important;
            max-width: none !important;
            aspect-ratio: auto !important;
            box-shadow: none !important;
            border: 0 !important;
            border-radius: 0 !important;
            page-break-after: avoid;
          }
        }
      `}</style>
    </div>
  );
}
